import { ChevronDown } from 'lucide-react'
import { LANGUAGE_META } from '../../utils/languages'

interface Props {
  value: string
  onChange: (lang: string) => void
  /** If omitted renders all known languages */
  options?: string[]
  /** Prepends an unset (empty-value) option — for callers where the language is optional. */
  allowNone?: boolean
  /** Label for the unset option. Only used when allowNone is set. */
  noneLabel?: string
  /** Accessible name; defaults to "Language". */
  ariaLabel?: string
}

export default function LanguageSelector({ value, onChange, options, allowNone, noneLabel, ariaLabel }: Props) {
  const langs = options ?? Object.keys(LANGUAGE_META)

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel ?? 'Language'}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          paddingLeft: 16,
          paddingRight: 32,
          paddingTop: 9,
          paddingBottom: 9,
          border: '1px solid var(--border)',
          borderRadius: 50,
          backgroundColor: 'var(--bg-surface)',
          color: 'var(--text-primary)',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: 'var(--font-ui)',
          cursor: 'pointer',
          outline: 'none',
          boxShadow: 'var(--shadow-card)',
          minWidth: 140,
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)' }}
        onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--border)' }}
      >
        {allowNone && <option value="">{noneLabel ?? '—'}</option>}
        {langs.map((code) => {
          // Names only: a flag emoji is not drawn on Windows, where it reads as «GB».
          const label = LANGUAGE_META[code]?.label ?? code.toUpperCase()
          return <option key={code} value={code}>{label}</option>
        })}
      </select>

      <ChevronDown
        size={14}
        style={{
          position: 'absolute',
          right: 10,
          color: 'var(--text-secondary)',
          pointerEvents: 'none',
          flexShrink: 0,
        }}
        aria-hidden
      />
    </div>
  )
}
